import { DocumentType, ResponseDocumentListItem } from '@/schema'

import { TflMetaWithRowNumber } from '../columns'

const stripExt = (name: string) => name.replace(/\.[^.]+$/, '')

type ParsedName = {
  kind: 'structured' | 'other'
  prefix?: string
  parts?: number[]
  raw: string
}
// | { kind: 'other'; raw: string }

const parseName = (name: string): ParsedName => {
  const raw = stripExt(name).trim()

  // Match like "T8.1.2", "L2.2", etc.
  const m = /^([A-Za-z])(\d+(?:\.\d+)*)/.exec(raw)
  if (!m) {
    return { kind: 'other', raw }
  }

  const prefix = m[1]
  const parts = m[2].split('.').map(Number)
  return { kind: 'structured', prefix, parts, raw }
}

const getDocTypePriority = (documentType: string): number => {
  switch (documentType.toLowerCase()) {
    case DocumentType.TABLE:
      return 1
    case DocumentType.FIGURE:
      return 2
    case DocumentType.LIST:
      return 3

    default:
      return 4 // untyped
  }
}

export const compareDocs = (
  aDocument: ResponseDocumentListItem | TflMetaWithRowNumber,
  bDocument: ResponseDocumentListItem | TflMetaWithRowNumber
): number => {
  const typeA = getDocTypePriority(
    (aDocument as ResponseDocumentListItem).document_type ||
      (aDocument as TflMetaWithRowNumber)?.documentType ||
      ''
  )
  const typeB = getDocTypePriority(
    (bDocument as ResponseDocumentListItem).document_type ||
      (bDocument as TflMetaWithRowNumber)?.documentType ||
      ''
  )
  const aName =
    (aDocument as ResponseDocumentListItem).display_name ||
    (aDocument as TflMetaWithRowNumber).displayName ||
    ''
  const bName =
    (bDocument as ResponseDocumentListItem).display_name ||
    (bDocument as TflMetaWithRowNumber).displayName ||
    ''

  // 1) Sort by Document Type first
  if (typeA !== typeB) return typeA - typeB

  // 2) If same type → use the previous natural order logic
  const a = parseName(aName)
  const b = parseName(bName)

  if (a.kind !== b.kind) return a.kind === 'structured' ? -1 : 1

  if (a.kind === 'other') {
    return a.raw.localeCompare(b.raw, undefined, {
      numeric: true,
      sensitivity: 'base',
    })
  }

  // compare prefix (T vs L vs F)
  if (a.prefix !== b.prefix) {
    return a.prefix?.localeCompare(b.prefix ?? '') ?? 0
  }

  // compare numeric parts
  const len = Math.max(a.parts?.length ?? 0, b.parts?.length ?? 0)
  for (let i = 0; i < len; i++) {
    const av = a.parts?.[i] ?? 0
    const bv = b.parts?.[i] ?? 0
    if (av !== bv) return av - bv
  }

  // shorter wins (T8.1 < T8.1.2)
  if (a.parts?.length !== b.parts?.length) {
    return (a.parts?.length ?? 0) - (b.parts?.length ?? 0)
  }

  return a.raw.localeCompare(b.raw, undefined, {
    numeric: true,
    sensitivity: 'base',
  })
}
